import { Grid, GridItem } from "@chakra-ui/react";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  FileCheck,
  FilePlus,
  Target,
} from "lucide-react";
import StatCard from "../../../../components/StatCard";
import type {
  MetricsDetail,
  RetrieveMetricsOutput,
} from "../../../../types/types";
import CollectionComparativeGraph from "./CollectionComparativeGraph";
import SwitchStatCard from "./SwitchStatCard";

type Props = {
  metrics: RetrieveMetricsOutput;
  detailMetrics: MetricsDetail;
};

const parseNumber = (value: string | number): number => {
  if (typeof value === "string") {
    return parseFloat(value.replace(/\./g, ""));
  }
  return value;
};

const StatsGrid = ({ metrics, detailMetrics }: Props): JSX.Element => {
  const {
    created_invoices = 0,
    created_invoices_monthly_variance = 0,
    expected_collection = 0,
    expected_collection_monthly_variance = 0,
    obtained_collection = 0,
    obtained_collection_monthly_variance = 0,
    pending_collection = 0,
    pending_collection_pct = 0,
    paid_invoices = 0,
    paid_invoices_monthly_variance = 0,
    pending_invoices = 0,
    pending_invoices_monthly_variance = 0,
  } = detailMetrics;

  const { daily_distribution = [], daily_distribution_previous_month = [] } =
    metrics;

  const parsedExpectedCollection = parseNumber(expected_collection);
  const parsedObtainedCollection = parseNumber(obtained_collection);
  const parsedPendingCollection = parseNumber(pending_collection);

  const obtainedShare =
    Math.round((parsedObtainedCollection / parsedExpectedCollection) * 100) ||
    0;
  const pendingShare =
    Math.round((parsedPendingCollection / parsedExpectedCollection) * 100) || 0;
  const paidInvoicesShare =
    Math.round((paid_invoices / created_invoices) * 100) || 0;
  const pendingInvoicesShare =
    Math.round((pending_invoices / created_invoices) * 100) || 0;

  const mergedDailyDistribution = Object.entries(daily_distribution || {}).map(
    ([day, currentValue]) => {
      const previousValue = (
        daily_distribution_previous_month as Record<string, number | undefined>
      )?.[day];
      return { day, currentMonth: currentValue, previousMonth: previousValue };
    }
  );

  return (
    <Grid
      templateColumns="repeat(3, 1fr)"
      autoRows="minmax(80px, auto)"
      gap={4}
      p="1px"
    >
      <StatCard
        title="Recaudo Esperado"
        icon={Target}
        stat={expected_collection}
        percentages={{
          value: Math.abs(expected_collection_monthly_variance),
          type:
            expected_collection_monthly_variance > 0 ? "increase" : "decrease",
        }}
        key="recaudo-esperado"
      />
      <StatCard
        title="Recaudo Obtenido"
        icon={CheckCircle}
        stat={obtained_collection}
        progress={{
          value: obtainedShare,
          label: `${obtainedShare}% del esperado`,
          color: "#3CB18B",
        }}
        percentages={{
          value: Math.abs(obtained_collection_monthly_variance),
          type:
            obtained_collection_monthly_variance > 0 ? "increase" : "decrease",
        }}
        key="recaudo-obtenido"
      />
      <StatCard
        title="Recaudo Pendiente"
        icon={Clock}
        stat={pending_collection}
        progress={{
          value: pendingShare,
          label: `${pendingShare}% restante`,
          color: "#FBBF24", // Yellow
        }}
        percentages={{
          value: Math.abs(pending_collection_pct),
          type: pending_collection_pct > 0 ? "increase" : "decrease",
        }}
        key="recaudo-pendiente"
      />
      <StatCard
        title="Facturas Creadas"
        icon={FilePlus}
        stat={created_invoices}
        percentages={{
          value: Math.abs(created_invoices_monthly_variance),
          type: created_invoices_monthly_variance > 0 ? "increase" : "decrease",
        }}
        key="facturas-creadas"
      />
      <StatCard
        title="Facturas Pagadas"
        icon={FileCheck}
        stat={paid_invoices}
        progress={{
          value: paidInvoicesShare,
          label: `${paidInvoicesShare}% tasa de pago`,
          color: "#3CB18B",
        }}
        percentages={{
          value: Math.abs(paid_invoices_monthly_variance),
          type: paid_invoices_monthly_variance > 0 ? "increase" : "decrease",
        }}
        key="facturas-pagadas"
      />
      <StatCard
        title="Facturas Pendientes"
        icon={AlertCircle}
        stat={pending_invoices}
        progress={{
          value: pendingInvoicesShare,
          label: `${pendingInvoicesShare}% tasa de pendiente`,
          color: "#EC5858", // Red
        }}
        percentages={{
          value: Math.abs(pending_invoices_monthly_variance),
          type: pending_invoices_monthly_variance > 0 ? "increase" : "decrease",
        }}
        key="facturas-pendientes"
      />
      <GridItem
        colSpan={1}
        rowSpan={2}
        bg="white"
        borderRadius="8px"
        boxShadow="rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
        p={"1.5rem"}
      >
        <SwitchStatCard
          total={obtained_collection}
          paymentMethods={metrics.by_payment_method}
          source={metrics.by_payment_source}
          key="recaudo-obtenido-switch"
        />
      </GridItem>
      <GridItem
        colSpan={2}
        rowSpan={2}
        bg="white"
        borderRadius="8px"
        boxShadow="rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
        p={"1.5rem"}
        key="recaudo-comparative"
      >
        <CollectionComparativeGraph data={mergedDailyDistribution} />
      </GridItem>
    </Grid>
  );
};

export default StatsGrid;
